// FileUploadWidget.js
import React, { useEffect, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, ListGroup, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axiosInstance from '../axiosInstance';

const FileUploadWidget = ({ unitId, actionAreaId }) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [error, setError] = useState(null);

    // Fetch the list of already uploaded files
    const fetchFiles = useCallback(async () => {
        setLoadingFiles(true);
        setError(null);
        try {
            const response = await axiosInstance.get(
                `/api/units/${unitId}/actionareas/${actionAreaId}/files/`
            );

            setFiles(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Error fetching files.');
        } finally {
            setLoadingFiles(false);
        }
    }, [unitId, actionAreaId]);

    useEffect(() => {
        if (unitId && actionAreaId) {
            fetchFiles();
        }
    }, [unitId, actionAreaId, fetchFiles]);

    // Handle file drop/upload
    const onDrop = useCallback(
        async (acceptedFiles) => {
            if (acceptedFiles.length === 0) return;
            setUploading(true);
            setError(null);
            const file = acceptedFiles[0]; // Only one file is uploaded at a time.
            const formData = new FormData();
            formData.append('file_upload', file);
            try {
                await axiosInstance.post(
                    `/api/units/${unitId}/actionareas/${actionAreaId}/files/`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );
                // Refresh file list after a successful upload
                fetchFiles();
            } catch (err) {
                setError(err.response?.data?.detail || 'Error uploading file.');
            } finally {
                setUploading(false);
            }
        },
        [unitId, actionAreaId, fetchFiles]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    // Delete a file from the server and update UI
    const handleDeleteFile = async (fileId) => {
        setError(null);
        try {
            await axiosInstance.delete(
                `/api/units/${unitId}/actionareas/${actionAreaId}/files/${fileId}/`
            );
            setFiles((prevFiles) => prevFiles.filter((f) => f.id !== fileId));
        } catch (err) {
            setError(err.response?.data?.detail || 'Error deleting file.');
        }
    };

    // Trigger download of the file by creating a temporary link
    const handleDownloadFile = (fileUrl) => {
        const link = document.createElement('a');
        link.href = fileUrl;
        // If the backend does not send a Content-Disposition header,
        // you can set a default filename here if needed.
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getFileNameFromUrl = (url) => {
        if (!url) return 'Unknown file';
        
        try {
            // Create a URL object to parse the URL
            const urlObj = new URL(url);
            
            // Get the pathname (without query parameters)
            const pathname = urlObj.pathname;
            
            // Split the pathname by '/' and get the last segment
            const pathParts = pathname.split('/');
            let fileName = pathParts[pathParts.length - 1];
            
            // Decode the URI component to handle special characters
            fileName = decodeURIComponent(fileName);
            
            return fileName;
        } catch (error) {
            // Fallback to the old method if URL parsing fails
            const urlParts = url.split('/');
            const lastPart = urlParts[urlParts.length - 1].split('?')[0]; // Remove query params
            return decodeURIComponent(lastPart);
        }
    };

    return (
        <div className="file-upload-widget">
            <div
                {...getRootProps()}
                className="border p-3 mb-2 text-center"
                style={{
                    cursor: 'pointer',
                    backgroundColor: isDragActive ? '#f0f0f0' : '#fff',
                    borderRadius: '4px',
                }}
            >
                <input {...getInputProps()} />
                {uploading ? (
                    <Spinner animation="border" size="sm" />
                ) : (
                    <span>Datei hier ablegen oder klicken, um eine auszuwählen</span>
                )}
            </div>
            {error && <div className="text-danger mb-2">{error}</div>}
            {loadingFiles ? (
                <div className="text-center">
                    <Spinner animation="border" size="sm" />
                </div>
            ) : (
                <ListGroup>
                    {files.length > 0 ? (
                        files.map((file) => (
                            <ListGroup.Item
                                key={file.id}
                                className="d-flex align-items-center justify-content-between"
                            >
                                <span>{getFileNameFromUrl(file.file_upload)}</span>
                                <div>
                                    <Button
                                        variant="link"
                                        onClick={() => handleDownloadFile(file.file_upload)}
                                    >
                                        Download
                                    </Button>
                                    <Button variant="link" onClick={() => handleDeleteFile(file.id)}>
                                        Löschen
                                    </Button>
                                </div>
                            </ListGroup.Item>
                        ))
                    ) : (
                        <ListGroup.Item className="text-muted">Keine Dateien hochgeladen.</ListGroup.Item>
                    )}
                </ListGroup>
            )}
        </div>
    );
};

export default FileUploadWidget;
